import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { StatoVuoto, IllustrazioneDocumenti } from "@/components/Illustrazioni";
import { toast } from "sonner";
import { Inbox, ExternalLink, Check, RefreshCw, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

const quando = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

// Documenti arrivati dai clienti tramite il portale esterno (finiscono direttamente su Drive).
function usePersistedBool(key, def) {
  const [v, setV] = useState(() => {
    const s = localStorage.getItem(key);
    return s === null ? def : s === "1";
  });
  return [v, (nv) => { localStorage.setItem(key, nv ? "1" : "0"); setV(nv); }];
}

export default function NuoviDocumenti({ senzaCornice }) {
  const qc = useQueryClient();
  const [apertoLocale, setAperto] = usePersistedBool("home_nuovi_documenti_open", true);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({
    queryKey: ["nuovi-documenti"],
    queryFn: async () => (await api.get("/dashboard/nuovi-documenti")).data,
    refetchInterval: 1000 * 60 * 5,
  });

  const aggiorna = () => qc.invalidateQueries({ queryKey: ["nuovi-documenti"] });

  const controlla = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/dashboard/nuovi-documenti/controlla");
      toast.success(r.nuovi ? `${r.nuovi} nuovo/i documento/i trovato/i` : "Nessun nuovo documento dai clienti");
      aggiorna();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Controllo non riuscito");
    } finally { setBusy(false); }
  };

  const visto = async (payload, msg) => {
    await api.post("/dashboard/nuovi-documenti/visto", payload);
    toast.success(msg);
    aggiorna();
  };

  const aperto = senzaCornice ? true : apertoLocale;
  const totale = data?.totale || 0;
  const ultimi = data?.ultimi || [];

  const azioni = (
    <button data-testid="nuovi-documenti-controlla" onClick={controlla} disabled={busy} className="btn btn-o btn-piccolo">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Controlla ora
    </button>
  );

  return (
    <div data-testid="nuovi-documenti-box" className={senzaCornice ? "" : "bg-white border border-[#1A2B4A]/10 rounded-sm p-4"}>
      {senzaCornice ? (
        <div className="flex flex-wrap items-center justify-between gap-2.5 mb-3">
          <span className="eti" data-testid="nuovi-documenti-badge">
            {totale > 0 ? `${totale} da lavorare` : "nessun documento da lavorare"}
          </span>
          {azioni}
        </div>
      ) : (
      <div className={`flex items-center justify-between gap-3 ${aperto ? "mb-4" : ""}`}>
        <button data-testid="nuovi-documenti-toggle" onClick={() => setAperto(!aperto)} className="flex items-center gap-2 text-left">
          {aperto ? <ChevronDown className="w-4 h-4 text-[#1A2B4A]" /> : <ChevronRight className="w-4 h-4 text-[#1A2B4A]" />}
          <Inbox className="w-4 h-4 text-[#C9A84C]" />
          <h3 className="font-serif text-xl text-[#1A2B4A]">Documenti ricevuti dai clienti</h3>
        </button>
        {aperto && azioni}
      </div>
      )}

      {aperto && (
      <>

      <p className="text-[11px] text-[#4a5568] mb-3">
        Ultimi invii dal <a href="https://clienti.robertabarbierato.com" target="_blank" rel="noreferrer"
          data-testid="link-portale-clienti" className="underline hover:text-[#1A2B4A]">portale clienti</a>
        {" · "}ultimo controllo: {quando(data?.ultimo_controllo)}
      </p>

      {ultimi.length === 0 ? (
        <StatoVuoto testid="nuovi-documenti-vuoto" illustrazione={IllustrazioneDocumenti}
          titolo="Nessun documento ricevuto dal portale finora"
          testo="Quando un cliente carica qualcosa dal portale, lo trovi qui con data e categoria." />
      ) : (
        <ul className="space-y-1 mb-4" data-testid="nuovi-documenti-cronologia">
          {ultimi.map((f) => (
            <li key={f.file_id}>
              <a href={f.link} target="_blank" rel="noreferrer" data-testid={`cronologia-${f.file_id}`}
                className="flex items-center gap-2.5 px-2 py-2 rounded-sm hover:bg-[#f9f8f5] transition-colors">
                <span className={`w-2 h-2 rounded-full shrink-0 ${f.visto ? "bg-[#1A2B4A]/15" : "bg-[#C9A84C]"}`}
                  title={f.visto ? "già letto" : "non letto"} />
                <span className={`text-sm truncate flex-1 ${f.visto ? "text-[#4a5568]" : "text-[#1A2B4A] font-semibold"}`}>
                  {f.name}
                </span>
                <span className="text-[11px] text-[#4a5568] shrink-0">{f.cliente} · {quando(f.modificato)}</span>
                {!f.visto && (
                  <button data-testid={`cronologia-visto-${f.file_id}`}
                    onClick={(e) => { e.preventDefault(); visto({ file_ids: [f.file_id] }, "Segnato come letto"); }}
                    className="text-[11px] text-[#4a5568] hover:text-[#276749] shrink-0">segna letto</button>
                )}
              </a>
            </li>
          ))}
        </ul>
      )}

      {totale > 0 && (
        <>
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8a6d1f] mb-2">Da lavorare ({totale})</p>
          <ul className="space-y-3">
            {data.clienti.map((c) => (
              <li key={c.cliente_id} data-testid={`nuovi-doc-cliente-${c.cliente_id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Link to={`/clienti?q=${encodeURIComponent(c.cliente || "")}`}
                    className="text-sm font-medium text-[#1A2B4A] hover:text-[#8a6d1f] truncate">{c.cliente}</Link>
                  <button data-testid={`nuovi-doc-visto-${c.cliente_id}`}
                    onClick={() => visto({ cliente_id: c.cliente_id }, "Documenti segnati come visti")}
                    className="inline-flex items-center gap-1 text-[11px] text-[#4a5568] hover:text-[#276749] shrink-0">
                    <Check className="w-3.5 h-3.5" /> visti
                  </button>
                </div>
                <ul className="space-y-1">
                  {c.files.map((f) => (
                    <li key={f.file_id}>
                      <a href={f.link} target="_blank" rel="noreferrer" data-testid={`nuovi-doc-file-${f.file_id}`}
                        className="flex items-center gap-2 text-xs text-[#1A2B4A] border-l-2 border-[#C9A84C] pl-2.5 py-1 hover:bg-[#f9f8f5] transition-colors">
                        <span className="truncate flex-1">{f.name}</span>
                        <span className="text-[10px] text-[#4a5568] shrink-0">{f.categoria} · {quando(f.modificato)}</span>
                        <ExternalLink className="w-3 h-3 shrink-0 text-[#4a5568]" />
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <button data-testid="nuovi-documenti-tutti-visti" onClick={() => visto({ tutti: true }, "Tutti i documenti segnati come visti")}
            className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-[#8a6d1f] hover:text-[#1A2B4A]">
            <Check className="w-3.5 h-3.5" /> Segna tutti come visti
          </button>
        </>
      )}
      </>
      )}
    </div>
  );
}
